import React, { useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { BRAND, footer } from "./content";
import "./site.css";

/* The site chrome: a sticky nav and a four-column footer.
   Scoped under .osv-site, which also owns the scroll container — the global
   stylesheet sets `body { overflow: hidden }`, so the site cannot scroll on
   the body. */

const SOCIALS: { name: string; href: string; d: string }[] = [
  {
    name: "LinkedIn",
    /* TODO(hari): real handles once the accounts exist. */
    href: "#",
    d: "M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9h4v12H3zM10 9h3.8v1.7h.05c.53-.95 1.83-1.95 3.76-1.95C21.3 8.75 22 11 22 14.1V21h-4v-6.1c0-1.45-.03-3.3-2.05-3.3-2.06 0-2.37 1.56-2.37 3.2V21h-4z",
  },
  {
    name: "Instagram",
    href: "#",
    d: "M12 2.2c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.8 3.8 0 0 1-1.38-.9 3.8 3.8 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.21 15.58 2.2 15.2 2.2 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.21 8.8 2.2 12 2.2zm0 3.05A6.75 6.75 0 1 0 18.75 12 6.75 6.75 0 0 0 12 5.25zm0 11.14A4.39 4.39 0 1 1 16.39 12 4.39 4.39 0 0 1 12 16.39zm6.95-11.4a1.58 1.58 0 1 1-1.58-1.58 1.58 1.58 0 0 1 1.58 1.58z",
  },
  {
    name: "YouTube",
    href: "#",
    d: "M21.6 7.2a2.5 2.5 0 0 0-1.76-1.77C18.25 5 12 5 12 5s-6.25 0-7.84.43A2.5 2.5 0 0 0 2.4 7.2 26 26 0 0 0 2 12a26 26 0 0 0 .4 4.8 2.5 2.5 0 0 0 1.76 1.77C5.75 19 12 19 12 19s6.25 0 7.84-.43a2.5 2.5 0 0 0 1.76-1.77A26 26 0 0 0 22 12a26 26 0 0 0-.4-4.8zM10 15.02V8.98L15.2 12z",
  },
  {
    name: "X",
    href: "#",
    d: "M17.53 3H20.5l-6.49 7.42L21.75 21h-5.97l-4.68-6.12L5.74 21H2.77l6.94-7.93L2.5 3h6.12l4.23 5.6zm-1.04 16.2h1.65L7.6 4.72H5.83z",
  },
];

/* The real artwork, generated from build_lockup.py — roundel, OPENSKY in
   Forum at +3%, and `vr` inside the broken rule. Never redraw this by hand:
   an approximation of the mark is not the mark. */
/* THE gold lockup, generated in an earlier session and approved then — the
   gradient in lockup-h-gold.svg is copied verbatim from lockup-gold.svg
   (stops, offsets, vector). Do not re-derive it. Metal needs 48px or more
   to read, so the nav carries it at 56px. */
/* THE approved file, copied straight out of D:\opensky-brandssets — the
   same lockup-gold.svg published in the "openskyvr — Logo Files" artifact on
   13 Oct. It is the STACKED lockup with the descriptor; the horizontal
   variants I built for the nav were wrong and are not used anywhere. */
/* The lockup as drawn in "The Logo as an Object" (7 Oct): the mark as SVG and
   the word, rule, vr and descriptor as HTML text — the engraving is a
   text-shadow, so the type cannot be an image. Geometry below is the crescent
   from build_lockup.py; see site.css for the plate and the engraving. */
const CRESCENT = (() => {
  const CX = 32, CY = 32, R = 25;
  const rad = (d: number) => (d * Math.PI) / 180;
  const easeOut = (t: number) => 1 - Math.pow(1 - t, 2);
  const a0 = 120, sweep = 250, w0 = 5.4, w1 = 0.4, steps = 170;
  const out: string[] = [], inn: string[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps, a = rad(a0 + sweep * t), w = w0 + (w1 - w0) * easeOut(t);
    out.push(`${(CX + Math.cos(a) * R).toFixed(2)} ${(CY + Math.sin(a) * R).toFixed(2)}`);
    inn.push(`${(CX + Math.cos(a) * (R - w)).toFixed(2)} ${(CY + Math.sin(a) * (R - w)).toFixed(2)}`);
  }
  return `M${out.join(" L")} L${inn.reverse().join(" L")} Z`;
})();

function Lockup({ mark = 40, full = true, horizontal = false }:
  { mark?: number; full?: boolean; horizontal?: boolean }) {
  return (
    <span className={`osv-lk${horizontal ? " h" : ""}`}>
      <svg viewBox="0 0 64 64" width={mark} height={mark} aria-hidden="true">
        <g transform="translate(32 32) scale(.86) translate(-32 -32)" fill="currentColor">
          <path d={CRESCENT} />
          <rect x="17.8" y="32.5" width="6.6" height="10.5" />
          <rect x="25.2" y="26" width="4.2" height="17" />
          <rect x="26.1" y="23.2" width="2.4" height="2.8" />
          <rect x="30.2" y="30" width="7" height="13" />
          <rect x="37.4" y="24" width="3.4" height="19" />
          <rect x="42" y="34.4" width="5.4" height="8.6" />
          <rect x="15.5" y="43" width="33" height="1.9" />
          <rect x="38.5" y="8.05" width="1.3" height="15.95" />
        </g>
        <rect x="37.59" y="6.6" width="1.12" height="5.4" fill="currentColor" />
        <circle className="beacon" cx="38.15" cy="5.1" r="1.95" fill="currentColor" />
      </svg>
      <span className="stack">
        <span className="word">opensky</span>
        <span className="rule"><span /><i className="vr">vr</i><span /></span>
        {full && <span className="desc">property, experienced</span>}
      </span>
    </span>
  );
}

export function Nav() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const close = () => setOpen(false);

  return (
    <nav className={`osv-nav${open ? " open" : ""}`}>
      <Link to="/next" className="brand" onClick={close} aria-label={BRAND}>
        <Lockup mark={52} full={false} horizontal />
      </Link>

      <button className="burger" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label="Menu">
        {open ? "Close" : "Menu"}
      </button>

      <div className="links">
        {/* Anchors, not pages: the audiences live on the home page until one of
            them converts badly enough to need a longer argument of its own. */}
        <a href="/next#film" onClick={close}>The walkthrough</a>
        <a href="/next#platform" onClick={close}>Platform</a>
        <a href="/next#who" onClick={close}>Who it's for</a>
        {/* A real page load, NOT a router Link. /login is rendered by
            RenderOnAnonymous, which sits outside the router and reads
            window.location.pathname once — a client-side navigation changes
            the URL without re-running it, so you land in the authenticated
            shell instead of the login form. Verified 2026-10-23. */}
        <a href="/login" onClick={close} className={pathname === "/login" ? "here" : ""}>
          Sign in
        </a>
        <a href="/next#talk" onClick={close} className="cta">
          Talk to us
        </a>
      </div>
    </nav>
  );
}

export function Footer() {
  return (
    <footer className="osv-foot">
      <div>
        <span className="osv-plate"><Lockup mark={48} /></span>
        <p className="sig">{footer.sig}</p>

        {/* Class, not inline styles: at 34px these were below the 44px touch
            minimum and an inline style is the one thing a media query cannot
            reach. The sizing now lives in site.css under pointer: coarse. */}
        <div className="osv-soc">
          {SOCIALS.map((s) => (
            <a
              key={s.name}
              href={s.href}
              aria-label={s.name}
              title={s.name}
              target="_blank"
              rel="noreferrer"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
                <path d={s.d} />
              </svg>
            </a>
          ))}
        </div>
      </div>

      {footer.columns.map((c) => (
        <div key={c.h}>
          <h4>{c.h}</h4>
          <ul>
            {c.items.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </div>
      ))}

      <div style={{ gridColumn: "1 / -1", borderTop: "1px solid var(--rule)", paddingTop: 22, marginTop: 10 }}>
        <span className="eyebrow">
          © {new Date().getFullYear()} {BRAND} · Hyderabad, India
        </span>
      </div>
    </footer>
  );
}

/* The app ships ONE viewport meta for every route, and it carries
   `user-scalable=no, maximum-scale=1`. That is right for the VR viewer, where
   a stray pinch would fight the 3D canvas for the gesture — and wrong for a
   marketing page a buyer reads on a phone, where refusing to zoom is both an
   annoyance and an accessibility failure (WCAG 1.4.4).

   A meta tag cannot be scoped by route in CSS, so the marketing site swaps it
   while it is mounted and puts the original back on the way out. `viewport-fit`
   is preserved either way, because the safe-area padding in site.css depends
   on it. Added 2026-10-23 after measuring on 18 device viewports. */
const ZOOMABLE = "width=device-width, initial-scale=1, viewport-fit=cover";

function useZoomableViewport() {
  React.useEffect(() => {
    const tag = document.querySelector('meta[name="viewport"]');
    if (!tag) return undefined;
    const previous = tag.getAttribute("content");
    tag.setAttribute("content", ZOOMABLE);
    return () => {
      if (previous === null) tag.removeAttribute("content");
      else tag.setAttribute("content", previous);
    };
  }, []);
}

export default function Shell() {
  useZoomableViewport();
  return (
    <div className="osv-site">
      <Nav />
      <Outlet />
      <Footer />
    </div>
  );
}
